import { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { type ConsultingOffer as Offer } from '../../types';
import { trackEvent } from '@/shared/analytics';
import { requestConsulting } from '../../api';
import WhatsAppIcon from '@/shared/components/WhatsAppIcon';

interface Props {
  offer: Offer;
  alreadyRequested: boolean;
}

export default function ConsultingOffer({ offer, alreadyRequested }: Props) {
  const [requestFailed, setRequestFailed] = useState(false);

  useEffect(() => {
    trackEvent('consulting_viewed');
  }, []);

  const handleClick = () => {
    trackEvent('consulting_whatsapp_clicked');
    if (alreadyRequested) return;

    setRequestFailed(false);
    requestConsulting().catch(() => {
      setRequestFailed(true);
      trackEvent('consulting_request_failed');
    });
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-slate-900">
        <FormattedMessage id="preparation.consulting.title" />
      </h3>
      <p className="mt-4 text-base leading-7 text-slate-600">
        <FormattedMessage id="preparation.consulting.body" />
      </p>

      <div className="mt-8 flex flex-col items-start gap-6">
        <div>
          <span className="text-sm font-medium text-slate-500">
            <FormattedMessage id="preparation.consulting.priceLabel" />
          </span>
          <p className="mt-1 flex items-baseline gap-2">
          <span className="text-4xl font-bold tracking-tight text-slate-900">
            <FormattedNumber
              value={offer.cost_cents / 100}
              style="currency"
              currency={offer.currency}
            />
          </span>
          <span className="text-sm font-medium text-slate-500">
            <FormattedMessage id="preparation.consulting.perProcess" />
          </span>
          </p>
        </div>

        <a
          href={offer.whatsapp_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#15803D] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#166534] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#15803D]/40"
        >
          <WhatsAppIcon className="h-4 w-4" />
          <FormattedMessage id="preparation.consulting.cta" />
        </a>
      </div>

      {requestFailed && (
        <p className="mt-3 text-sm text-red-600">
          <FormattedMessage id="preparation.consulting.requestError.message" />{' '}
          <button type="button" onClick={handleClick} className="underline underline-offset-2">
            <FormattedMessage id="preparation.consulting.requestError.retry" />
          </button>
        </p>
      )}
    </div>
  );
}
